import { css } from '@emotion/react';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, type ReactElement, useCallback, useMemo, useState,
} from 'react';
import { AccordionComponent } from '~/_shared/baseComponents/accordion';
import { ButtonComponent } from '~/_shared/baseComponents/buttons/button/button.component';
import { type DropdownOption } from '~/_shared/baseComponents/dropdown';
import { OutlinePanelComponent } from '~/_shared/baseComponents/outlinePanel/outlinePanel.component';
import { OutlinePanelOptions } from '~/_shared/baseComponents/outlinePanel/outlinePanel.constants';
import { StackedWarningsComponent } from '~/_shared/baseComponents/warning/stackedWarnings.component';
import {
  getMatchUpColumnRolesForSection, getMatchUpDataHeaderForSection,
} from '~/_shared/components/matchUpDataModal/matchUpDataModal.helpers';
import type { ConfirmationModalProps } from '~/_shared/components/modal/confirmation/confirmationModal.component';
import { ADMIN_MAP_PAGES } from '~/_shared/components/modal/modal.constants';
import type { Theme } from '~/_shared/themes/theme.model';
import { filterAddedSpreadsheetColumns } from '~/_shared/types/spreadsheetData/spreadsheetColumns.helpers';
import {
  KeyboardKeys, useKeyPress,
} from '~/_shared/utils/hooks/useKeyPress';
import { stringObjectEntries } from '~/_shared/utils/object/objectEnumerators';
import {
  bannedBaseColumnNames, bannedGeocoordinateColumnNames,
} from '~/map/page/matchup/matchupData.constants';
import type { WithoutModalProps } from '~/modal/modalRegistry.type';
import { type ModalProps } from '~/modal/modalType.enum';
import { useModal } from '~/modal/useModal.hook';
import {
  s, Trans,
} from '~/translations/Trans';
import {
  ColumnRole, type ColumnsMatchUp, createColumnRoleName, isAddressColumnRole, isLatLngColumnRole,
} from '../../types/columnRole.enum';
import {
  type TranslationFnc, useTranslation,
} from '../../utils/hooks';
import { ModalComponent } from '../modal/modal.component';
import { MatchUpDataSection } from './matchUpDataModalSection.enum';
import {
  MatchUpDataTableComponent, type RowProps,
} from './matchUpDataTable.component';

export const MATCHUP_MODAL_WIDTH = 547;

const contentPadding = 15;

export const matchUpDataAllDataSections = [MatchUpDataSection.LocationData, MatchUpDataSection.InfoPopup];
export const matchupDataOnlyInfoPanelSection = [MatchUpDataSection.InfoPopup];

const textStyle = css({
  cursor: 'default',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
});

const infoTextStyle = css(textStyle, {
  padding: contentPadding,
});

const contentStyle = css({
  padding: 0,
});

const itemButtonStyle = css({
  textTransform: 'uppercase',
});

const footerStyle = css({
  width: '100%',
});

const submitButtonWrapperStyle = css({
  display: 'flex',
  justifyContent: 'flex-end',
});

const submitWarningStyle = css({
  marginBottom: 20,
});

const proceedStyle = (theme: Theme) => css({
  background: theme.buttonColors.primaryBackground,
  border: `1px solid ${theme.buttonColors.defaultBorder}`,
  borderRadius: 4,
  color: theme.textColors.quaternary,
  fontWeight: 400,
  padding: '0 2px',
});

const getIsColumnReuseAllowedForRole = (role: ColumnRole): boolean =>
  [ColumnRole.Name].includes(role);

const getAvailableColumns = (
  columns: ReadonlyArray<[string, string]>,
  matchUp: ColumnsMatchUp,
  forRole: ColumnRole,
): ReadonlyArray<[string, string]> => {
  if (getIsColumnReuseAllowedForRole(forRole)) {
    return columns;
  }

  const usedColumnIds: ReadonlySet<string | null> = new Set<string | null>(
    stringObjectEntries(matchUp).filter(([role]) =>
      !getIsColumnReuseAllowedForRole(role)
    ).map(([_, value]) => value[0])
  );
  return columns.filter(c => !usedColumnIds.has(c[1]));
};

const convertColumnsToDropdownOptions = (columns: ReadonlyArray<[string, string]>): ReadonlyArray<DropdownOption<string>> =>
  columns.map(c => ({ name: c[0], value: c[1] }));

const createOnChange = (
  matchUp: ColumnsMatchUp,
  onChange: (newMatchUp: ColumnsMatchUp, role: ColumnRole) => void,
  role: ColumnRole
) =>
  (newColumnId: string) => onChange({ ...matchUp, [role]: [newColumnId, false] }, role);

const addSelectedColumn = (props: RowCreationProps, columns: ReadonlyArray<[string, string]>, selectedColumnId: null | string) => {
  const selectedColumn = selectedColumnId !== null && props.columns.find(c => c[1] === selectedColumnId);

  return selectedColumn
    ? [...columns.filter(c => c[1] !== selectedColumnId), selectedColumn]
    : columns;
};

type RowCreationProps = Readonly<{
  columns: ReadonlyArray<[string, string]>;
  matchUp: ColumnsMatchUp;
  onChange: (matchUp: ColumnsMatchUp, role: ColumnRole) => void;
}>;

const createRowProps = (props: RowCreationProps, roles: ReadonlyArray<ColumnRole>, t: TranslationFnc): ReadonlyArray<RowProps> => {
  return roles.map(role => {
    const availableColumns = getAvailableColumns(props.columns, props.matchUp, role);
    const columns = addSelectedColumn(
      props,
      availableColumns,
      props.matchUp[role][0],
    );

    const dropdownOptions = [
      { value: undefined, name: t('No Match') },
      ...filterAddedSpreadsheetColumns(convertColumnsToDropdownOptions(columns)),
    ];

    return {
      onChange: createOnChange(props.matchUp, props.onChange, role),
      dropdownOptions,
      selectedOptionId: props.matchUp[role][0],
      rowName: createColumnRoleName(role, t),
      isAutoMatched: props.matchUp[role][1],
    };
  });
};

export type MatchUpDataProps = Readonly<{
  columns: ReadonlyArray<[string, string]>;
  matchUp: ColumnsMatchUp;
  hideLatLng?: boolean;
  hideHeading?: boolean;
  isPublicApiActive: boolean;
  sections: ReadonlyArray<MatchUpDataSection>;

  onSave: (newMatchUp: ColumnsMatchUp) => void;
}>;

export const MatchUpDataComponent: FC<MatchUpDataProps> = (props) => {
  const [t] = useTranslation();
  const [displayedSections, setDisplayedSections] = useState<ReadonlyArray<MatchUpDataSection>>([...props.sections]);
  const { openModal: openConfirmation, closeModal: closeConfirmation } = useModal('Confirmation');

  const columnsLookup = useMemo(() =>
    new Map(props.columns.map(column => [column[1], column[0]])),
  [props.columns]);

  const toggleDisplaySection = (part: MatchUpDataSection) => displayedSections.includes(part)
    ? setDisplayedSections(displayedSections.filter(p => p !== part))
    : setDisplayedSections([...displayedSections, part]);

  const handleChangeRow = useCallback((newMatchup: ColumnsMatchUp, newRole: ColumnRole) => {
    const save = () => props.onSave(newMatchup);
    let reason: string | null = null;

    const columnId = newMatchup[newRole][0];
    const columnName = columnId && columnsLookup.get(columnId);

    const columnNameIncluded = (subStr: string) => columnName?.toLowerCase()?.includes(subStr);
    const issueOptions = { columnName, categoryName: createColumnRoleName(newRole, t) };

    // no "else" between cases here as one column can belong to multiple role groups
    // so we want to check them all
    if (isAddressColumnRole(newRole)) {
      const potentialIssue = bannedBaseColumnNames.some(columnNameIncluded);

      if (potentialIssue) {
        reason = t('matchUpModal.confirmSelection.basic', issueOptions);
      }
    }

    if (isLatLngColumnRole(newRole)) {
      const potentialIssue = bannedGeocoordinateColumnNames.some(columnNameIncluded);
      if (potentialIssue) {
        reason = t('matchUpModal.confirmSelection.geocoordinates', issueOptions);
      }
    }

    const confirmationContent = (
      <span css={textStyle}>
        <div>{reason}</div>
        <div>
          <Trans i18nKey="matchUpModal.confirmSelection.proceed">
            {s('part1')}
            <span css={proceedStyle} />
            {s('part2')}
          </Trans>
        </div>
      </span>
    );

    if (reason) {
      openConfirmation(getConfirmationModalProps({
        content: confirmationContent,
        onModalClose: closeConfirmation,
        onSubmit: save,
        t,
      }));
    }
    else {
      save();
    }
  }, [closeConfirmation, columnsLookup, openConfirmation, props, t]);

  return (
    <>
      {!props.hideHeading && (
        <div css={infoTextStyle}>
          <div>{t('matchUpModal.infoMessage')}</div>
          <div>{t('matchUpModal.infoMessage.hint')}</div>
        </div>
      )}

      <OutlinePanelComponent outlines={[OutlinePanelOptions.Top]}>
        <AccordionComponent
          data={props.sections.map((section) => {
            const columnRole = getMatchUpColumnRolesForSection(section, props.hideLatLng, !props.isPublicApiActive);

            return ({
              header: getMatchUpDataHeaderForSection(section, t),
              isExpanded: displayedSections.includes(section),
              onHeadingClick: () => toggleDisplaySection(section),
              child: (
                <MatchUpDataTableComponent
                  primaryColumnHeading={t('Categories').toUpperCase()}
                  secondaryColumnHeading={t('Your Data').toUpperCase()}
                  rows={
                    createRowProps({ ...props, onChange: handleChangeRow }, columnRole, t)
                  }
                />
              ),
            });
          })}
          itemButtonStyle={itemButtonStyle}
          panelStyle={contentStyle}
          showCount
        />
      </OutlinePanelComponent>
    </>
  );
};

type MatchUpDataModalProps = ModalProps<MatchUpDataProps> & Readonly<{
  isCloseDisabled?: boolean;
  isSubmitDisabled: boolean;
  onSubmit: () => void;
}>;

export const MatchUpDataModalComponent: FC<MatchUpDataModalProps> = ({
  onClose,
  isOpen,
  isCloseDisabled,
  isSubmitDisabled,
  onSubmit,
  ...restProps
}) => {
  const [t] = useTranslation();

  useKeyPress(useMemo(() => ({
    callbacks: { onKeyPress: onSubmit },
    options: { allowActionWhenModalOpen: true, allowInputs: true },
    targetKeys: KeyboardKeys.Enter,
  }), [onSubmit]));

  return (
    <ModalComponent
      allowedPages={ADMIN_MAP_PAGES}
      onClose={onClose}
      isOpen={isOpen}
      isCloseDisabled={isCloseDisabled}
      contentStyle={contentStyle}
      maxWidth={MATCHUP_MODAL_WIDTH}
      caption={t('Categorize Data')}
      footer={(
        <div css={footerStyle}>
          {isSubmitDisabled && (
            <StackedWarningsComponent
              css={submitWarningStyle}
              warnings={[t('At least one of the columns: Address, City, State, Zip, Country, or both Latitude and Longitude columns must be selected to categorize the data.')]}
            />
          )}

          <div css={submitButtonWrapperStyle}>
            <ButtonComponent
              text={t('Done')}
              isDisabled={isSubmitDisabled}
              onClick={onSubmit}
              prefixIcon={faCheck}
            />
          </div>
        </div>
      )}
    >
      <MatchUpDataComponent {...restProps} />
    </ModalComponent>
  );
};

const getConfirmationModalProps = (props: {
  onModalClose: () => void;
  onSubmit: () => void;
  content: ReactElement;
  t: TranslationFnc;
}): WithoutModalProps<ConfirmationModalProps> => ({
  title: props.t('Categorize Data'),
  confirmCaption: props.t('Proceed'),
  cancelCaption: props.t('Cancel'),
  onClose: props.onModalClose,
  onCancel: props.onModalClose,
  onConfirm: () => {
    props.onSubmit();
    props.onModalClose();
  },
  children: props.content,
  isConfirmButtonDestructive: false,
});
